import { useCCF } from '@app/ccf/company-report/ccf-context';
import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { formatMultipleSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useGetOtherActivityList } from '@shared/api/hooks/ccf/activities/other';
import { OtherActivity } from '@shared/api/types';
import { Button } from '@shared/components/ui/button';
import { parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import AdditionalCategoryEditForm from './additional-category-edit-form';
import { additionalCategoryTableColumns } from './additional-category-table-columns';

interface AdditionalCategoryTableProps {
  activityTypeSlug: string;
}

export default function AdditionalCategoryTable({
  activityTypeSlug,
}: AdditionalCategoryTableProps) {
  const [search] = useQueryState('description', parseAsString);
  const { page, perPage, sort } = useDataTableQueryState();
  const [selectedActivityUuid, setSelectedActivityUuid] =
    useState<OtherActivity['uuid']>();

  const { uuid: reportUuid } = useCCF();

  const {
    showSimpleDialog: showEditDialog,
    SimpleDialogComponent: EditDialog,
    closeSimpleDialog: closeEditDialog,
  } = useSimpleDialog();

  const { data, isPreviousData, isFetchedAfterMount, isLoading } =
    useGetOtherActivityList(reportUuid!, activityTypeSlug, {
      page: page,
      page_size: perPage,
      search: search || '',
      sort: formatMultipleSortingForEndpoint(sort! || []),
    });

  const { results: activities, count } = data || {};

  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: additionalCategoryTableColumns,
    pageCount,
    getRowId: (row) => row.uuid,
  });

  return (
    <>
      <DataTable
        table={table}
        context={context}
        className="bg-gray-00"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          setSelectedActivityUuid(row?.original.uuid);
          showEditDialog();
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
      >
        <DataTableToolbar table={table}>
          <Button
            size="sm"
            onClick={() => {
              setSelectedActivityUuid(undefined);
              showEditDialog();
            }}
          >
            Add data
          </Button>
        </DataTableToolbar>
      </DataTable>
      {EditDialog({
        header: selectedActivityUuid ? 'Edit data' : 'Add data',
        content: (
          <AdditionalCategoryEditForm
            activityTypeSlug={activityTypeSlug}
            closeDialog={closeEditDialog}
            activityUuid={selectedActivityUuid}
          />
        ),
      })}
    </>
  );
}
